import React from 'react';
import { ComparisonResponseCard } from '../components/results/comparisons/ComparisonResponseCard';
import type { Response } from '../lib/types/results';

const baseTimestamp = new Date().toISOString();

const buildResponse = (overrides: Partial<Response>): Response => ({
  id: overrides.id ?? crypto.randomUUID(),
  comparisonId: 'chip-preview',
  modelId: overrides.modelId ?? 'gpt-4',
  providerId: overrides.providerId ?? 'openai',
  content:
    overrides.content ??
    'This preview uses mocked content to showcase the trophy and neutral score chips for the redesigned Comparison Detail view.',
  response: overrides.response,
  status: overrides.status ?? 'completed',
  tokenCount: overrides.tokenCount,
  tokenUsage: overrides.tokenUsage ?? null,
  latencyMs: overrides.latencyMs ?? 2400,
  cost: overrides.cost ?? null,
  scoreOverall: overrides.scoreOverall ?? null,
  scores: overrides.scores ?? null,
  createdAt: overrides.createdAt ?? baseTimestamp,
  updatedAt: overrides.updatedAt ?? baseTimestamp,
  errorMessage: overrides.errorMessage ?? null,
  model: overrides.model ?? null,
  provider: overrides.provider ?? null,
});

const previewResponses: Array<{ response: Response; score: number; role: 'winner' | 'nonwinner'; variant: 'winner-trophy-solid' | 'nonwinner-neutral' }> = [
  {
    response: buildResponse({
      id: 'resp-winner',
      modelId: 'nova-premier',
      providerId: 'anthropic',
      content:
        'Nova Premier provided the most detailed, policy-aligned answer with clear citations and balanced tone, so it earns the trophy chip in this preview.',
    }),
    score: 9.2,
    role: 'winner',
    variant: 'winner-trophy-solid',
  },
  {
    response: buildResponse({
      id: 'resp-runner',
      modelId: 'sonnet-3.5',
      providerId: 'anthropic',
      content:
        'Sonnet 3.5 delivered a concise summary but lacked depth on safeguards. The neutral non-winner chip keeps it in context without competing for attention.',
    }),
    score: 8.1,
    role: 'nonwinner',
    variant: 'nonwinner-neutral',
  },
  {
    response: buildResponse({
      id: 'resp-runner-2',
      modelId: 'gpt-4o-mini',
      providerId: 'openai',
      content:
        'GPT-4o mini covered the basics quickly, which is useful on mobile layouts. This card demonstrates how multiple non-winners align with the new chip geometry.',
    }),
    score: 7.8,
    role: 'nonwinner',
    variant: 'nonwinner-neutral',
  },
];

export const ChipPreviewPage: React.FC = () => {
  if (!import.meta.env.DEV) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <p className="text-sm text-slate-400">Chip preview is only available in development builds.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white py-10 px-4 md:px-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Dev Utilities</p>
          <h1 className="text-3xl font-semibold mt-2">Comparison Detail Chip Preview</h1>
          <p className="text-slate-400 mt-2">
            Use this page to snapshot the winner and non-winner score chips without wiring the API. All cards reuse the live
            <code className="mx-1 px-1 py-0.5 rounded bg-slate-800 text-cyan-300">ComparisonResponseCard</code>
            component so spacing, accessibility text, and telemetry match production.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {previewResponses.map(({ response, score, role, variant }) => (
            <ComparisonResponseCard
              key={response.id}
              response={response}
              score={score}
              scoreRole={role}
              scoreChipVariant={variant}
              isWinner={role === 'winner'}
              isHighestScore={role === 'winner'}
              comparisonId="chip-preview"
              comparisonCorrelationId="chip-preview-correlation"
              onClick={() => undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
